// Cache manager with TTL support and LocalStorage persistence
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
    this.STORAGE_KEY = 'flango_practice_cache';
    this._loadFromStorage();
  }

  // Load cache from localStorage
  _loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        // Parse JSON and convert array of entries back to Map
        const entries = JSON.parse(stored);
        this.cache = new Map(entries);

        // Initial cleanup of expired items upon load
        this.cleanup();
      }
    } catch (err) {
      console.error('Failed to load cache from storage:', err);
      // If load fails, start with empty cache
      this.cache = new Map();
    }
  }

  // Save cache to localStorage
  _saveToStorage() {
    try {
      // Convert Map to array of entries for JSON serialization
      // We do this synchronously as localStorage is synchronous
      const entries = Array.from(this.cache.entries());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(entries));
    } catch (err) {
      console.error('Failed to save cache to storage:', err);
    }
  }

  // Set cache with TTL
  set(key, value, ttl = this.defaultTTL) {
    const expiry = Date.now() + ttl;
    this.cache.set(key, {
      value,
      expiry,
    });
    this._saveToStorage();
  }

  // Get cache value
  get(key) {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      this._saveToStorage();
      return null;
    }

    return item.value;
  }

  // Check if key exists and is not expired
  has(key) {
    return this.get(key) !== null;
  }

  // Delete specific key
  delete(key) {
    const result = this.cache.delete(key);
    this._saveToStorage();
    return result;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this._saveToStorage();
  }

  // Clear expired entries
  cleanup() {
    const now = Date.now();
    let changed = false;
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiry) {
        this.cache.delete(key);
        changed = true;
      }
    }
    if (changed) {
      this._saveToStorage();
    }
  }

  // Get cache size
  size() {
    return this.cache.size;
  }

  // Get cache statistics
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const item of this.cache.values()) {
      if (now > item.expiry) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      total: this.cache.size,
      valid: validEntries,
      expired: expiredEntries,
    };
  }
}

// Global cache instance
export const cacheManager = new CacheManager();

// Cache keys for different data types
export const CACHE_KEYS = {
  MODULES: 'modules',
  MODULE: (id) => `module_${id}`,
  GROUP: (moduleId, position) => `group_${moduleId}_${position}`,
  WORDS: (groupId) => `words_${groupId}`,
  SENTENCES: (moduleId, groupId, type) => `sentences_${moduleId}_${groupId}_${type}`,
  STORY: (moduleId, groupId) => `story_${moduleId}_${groupId}`,
  SUMMARY: (moduleId, groupId) => `summary_${moduleId}_${groupId}`,
  FEEDBACK: (moduleId, groupId) => `feedback_${moduleId}_${groupId}`,
};

// Cache TTLs for different data types
export const CACHE_TTL = {
  MODULES: 2 * 60 * 1000, // 2 minutes
  MODULE: 5 * 60 * 1000, // 5 minutes
  GROUP: 5 * 60 * 1000, // 5 minutes
  WORDS: 5 * 60 * 1000, // 5 minutes
  SENTENCES: 30 * 60 * 1000, // 30 minutes
  STORY: 60 * 60 * 1000, // 1 hour
  SUMMARY: 60 * 60 * 1000, // 1 hour
  FEEDBACK: 60 * 60 * 1000, // 1 hour
};

// Utility functions for common cache operations
export const cacheUtils = {
  // Cache modules data
  cacheModules: (modules) => {
    cacheManager.set(CACHE_KEYS.MODULES, modules, CACHE_TTL.MODULES);
  },

  // Cache module data
  cacheModule: (moduleId, moduleData) => {
    cacheManager.set(CACHE_KEYS.MODULE(moduleId), moduleData, CACHE_TTL.MODULE);
  },

  // Cache group data
  cacheGroup: (moduleId, position, groupData) => {
    cacheManager.set(CACHE_KEYS.GROUP(moduleId, position), groupData, CACHE_TTL.GROUP);
  },

  // Cache words data
  cacheWords: (groupId, words) => {
    cacheManager.set(CACHE_KEYS.WORDS(groupId), words, CACHE_TTL.WORDS);
  },

  // Cache sentences data
  cacheSentences: (moduleId, groupId, type, sentences) => {
    cacheManager.set(CACHE_KEYS.SENTENCES(moduleId, groupId, type), sentences, CACHE_TTL.SENTENCES);
  },

  // Cache story data
  cacheStory: (moduleId, groupId, story) => {
    cacheManager.set(CACHE_KEYS.STORY(moduleId, groupId), story, CACHE_TTL.STORY);
  },

  // Cache summary data
  cacheSummary: (moduleId, groupId, summary) => {
    cacheManager.set(CACHE_KEYS.SUMMARY(moduleId, groupId), summary, CACHE_TTL.SUMMARY);
  },

  // Cache feedback data
  cacheFeedback: (moduleId, groupId, feedback) => {
    cacheManager.set(CACHE_KEYS.FEEDBACK(moduleId, groupId), feedback, CACHE_TTL.FEEDBACK);
  },

  // Get cached modules
  getCachedModules: () => {
    return cacheManager.get(CACHE_KEYS.MODULES);
  },

  // Get cached module
  getCachedModule: (moduleId) => {
    return cacheManager.get(CACHE_KEYS.MODULE(moduleId));
  },

  // Get cached group
  getCachedGroup: (moduleId, position) => {
    return cacheManager.get(CACHE_KEYS.GROUP(moduleId, position));
  },

  // Get cached words
  getCachedWords: (groupId) => {
    return cacheManager.get(CACHE_KEYS.WORDS(groupId));
  },

  // Get cached sentences
  getCachedSentences: (moduleId, groupId, type) => {
    return cacheManager.get(CACHE_KEYS.SENTENCES(moduleId, groupId, type));
  },

  // Get cached story
  getCachedStory: (moduleId, groupId) => {
    return cacheManager.get(CACHE_KEYS.STORY(moduleId, groupId));
  },

  // Get cached summary
  getCachedSummary: (moduleId, groupId) => {
    return cacheManager.get(CACHE_KEYS.SUMMARY(moduleId, groupId));
  },

  // Get cached feedback
  getCachedFeedback: (moduleId, groupId) => {
    return cacheManager.get(CACHE_KEYS.FEEDBACK(moduleId, groupId));
  },

  // Clear all cache for a module
  clearModuleCache: (moduleId) => {
    cacheManager.delete(CACHE_KEYS.MODULE(moduleId));
    // Clear related caches
    for (const [key] of cacheManager.cache.entries()) {
      if (key.includes(`group_${moduleId}_`) || key.includes(`words_${moduleId}_`)) {
        cacheManager.delete(key);
      }
    }
  },

  // Clear all cache for a group
  clearGroupCache: (moduleId, position) => {
    cacheManager.delete(CACHE_KEYS.GROUP(moduleId, position));
    // Clear related caches
    for (const [key] of cacheManager.cache.entries()) {
      if (key.includes(`words_${moduleId}_${position}`)) {
        cacheManager.delete(key);
      }
    }
  },

  // Clear sentences cache for a specific type
  clearSentencesCache: (moduleId, groupId, type) => {
    cacheManager.delete(CACHE_KEYS.SENTENCES(moduleId, groupId, type));
  },

  // Clear story cache
  clearStoryCache: (moduleId, groupId) => {
    cacheManager.delete(CACHE_KEYS.STORY(moduleId, groupId));
  },

  // Clear summary cache
  clearSummaryCache: (moduleId, groupId) => {
    cacheManager.delete(CACHE_KEYS.SUMMARY(moduleId, groupId));
  },

  // Clear feedback cache
  clearFeedbackCache: (moduleId, groupId) => {
    cacheManager.delete(CACHE_KEYS.FEEDBACK(moduleId, groupId));
  },

  // Clear all cache
  clearAllCache: () => {
    cacheManager.clear();
  },
};

// Auto-cleanup expired entries every 5 minutes
setInterval(() => {
  cacheManager.cleanup();
}, 5 * 60 * 1000);

export default cacheManager;
